'use client';

import { useMemo } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import { MarketId, MarketKey, UIMarket } from '@drift/common';
import { BN } from '@drift-labs/sdk';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';

export interface EffectiveMarginModeResult {
	mode: 'cross' | 'isolated';
	isLocked: boolean;
}

export function useEffectiveMarginMode(
	marketIdKey: MarketKey | undefined
): EffectiveMarginModeResult {
	const acct = useDriftAccountStore((s) => s.getCurrentUserAccount());
	const getMarginModeForMarket = useMarketSettingsStore(
		(s) => s.getMarginModeForMarket
	);
	const isIsolatedPositionsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_ISOLATED_POSITIONS')
	);
	const isolatedMarginEnabledMarkets = useFeatureFlagStore(
		(s) => s.flags?.ISOLATED_MARGIN_ENABLED_MARKETS?.marketSymbols ?? []
	);

	return useMemo<EffectiveMarginModeResult>(() => {
		if (!marketIdKey) {
			return { mode: 'cross', isLocked: false };
		}

		const marketId = MarketId.getMarketIdFromKey(marketIdKey);
		const user = acct?.client;

		// If perp and there is an open position, derive from position and lock
		if (user && marketId.isPerp) {
			const position = user.getPerpPosition(marketId.marketIndex);
			if (
				position &&
				position.baseAssetAmount &&
				position.baseAssetAmount.abs().gt(new BN(0))
			) {
				const isIsolated = user.isPerpPositionIsolated(position);
				return { mode: isIsolated ? 'isolated' : 'cross', isLocked: true };
			}

			// If there are open orders for this market, lock margin mode
			const hasOpenOrdersForMarket = user
				.getOpenOrders()
				.some((order) => order.marketIndex === marketId.marketIndex);
			if (hasOpenOrdersForMarket) {
				const stored = getMarginModeForMarket(marketIdKey, acct?.userKey ?? '');
				return { mode: stored ?? 'cross', isLocked: true };
			}
		}

		// Check if isolated margin is enabled for this specific market
		const isIsolatedEnabledForMarket = (() => {
			if (!isIsolatedPositionsEnabled) return false;
			if (!marketId.isPerp) return false;
			const marketSymbol = UIMarket.createPerpMarket(
				marketId.marketIndex
			).symbol;
			return isolatedMarginEnabledMarkets.includes(marketSymbol);
		})();

		// Fallback to stored preference; default to cross
		const stored = getMarginModeForMarket(marketIdKey, acct?.userKey ?? '');
		const preferredMode = stored ?? 'cross';

		// If isolated is not enabled for this market, force cross mode
		if (preferredMode === 'isolated' && !isIsolatedEnabledForMarket) {
			return { mode: 'cross', isLocked: false };
		}

		return { mode: preferredMode, isLocked: false };
	}, [
		acct,
		getMarginModeForMarket,
		marketIdKey,
		isIsolatedPositionsEnabled,
		isolatedMarginEnabledMarkets,
	]);
}

export default useEffectiveMarginMode;
